import { waitForMetricaClientId } from '../utils/metrica';
import {createApi, fetchBaseQuery} from "@reduxjs/toolkit/dist/query/react";
import { createSlice } from '@reduxjs/toolkit'
import { REHYDRATE } from "redux-persist";
import { useSelector } from "react-redux";

import { rootReducer } from "../store";
import { USER_GROUPS } from "../utils/constants";
import {API_URL, IS_AGREED_DEMO, ROUTES} from "../config/constants";
import { CALC_ID } from "./calc";
import {changeAppLanguage, getAcceptLanguage, getLanguageByIp, getStoredAppLanguage} from "../config/i18";
import {getIpInfo} from "../utils/ip";
import {getDemoApiResponse} from "../components/cabinet/demo/mock-api";
import {isDemoPage} from "../utils/hooks";

import {
  clearAuthSession,
  getRememberedSession,
  getSessionKey,
  getToken,
  isStoredSession,
  REFRESH_ENDPOINT,
  refreshRememberedSession,
  scheduleSessionRefresh,
} from './auth-session';

export { AUTH_TOKEN, getToken, setToken, saveSignInSession } from './auth-session';

export const BROKER = "broker";
export const CLIENT_ID = "clientId";

export const removeToken = () => {
  clearAuthSession();
  localStorage.removeItem(CALC_ID);
}

const isPublicAuthPage = () => isDemoPage() || window.location.pathname === ROUTES.LOGIN;

const redirectToLogin = () => {
  localStorage.setItem(IS_AGREED_DEMO, 'false');
  window.location.href = ROUTES.LOGIN;
};

const expireRememberedSession = (id: string) => {
  if (!isStoredSession(id)) return;

  removeToken();
  if (!isPublicAuthPage()) redirectToLogin();
};

export const getRequestLanguage = async (user?: MeParamsOutput) => {
  const storedLanguage = getStoredAppLanguage();

  if (isDemoPage() && storedLanguage) return storedLanguage;

  if (user?.lang) return user.lang;

  if (storedLanguage) return storedLanguage;

  const languageByIp = await getLanguageByIp();
  changeAppLanguage(languageByIp);

  return languageByIp;
}

export function baseQuery(navigate?: (path: string) => void) {

  const rawBaseQuery = fetchBaseQuery({
    baseUrl: API_URL,
    prepareHeaders: async (headers, { endpoint, getState }) => {
      const token = getToken();
      const user = (getState() as ReturnType<typeof rootReducer>)?.userParams?.me;
      const requestLanguage = await getRequestLanguage(user);

      if (token && endpoint !== REFRESH_ENDPOINT) headers.set('authorization', `Bearer ${token}`);
      headers.set('Accept-Language', getAcceptLanguage(requestLanguage));
      return headers;
    },
  });

  return async (args: any, api: any, extraOptions: any) => {
    const demoResponse = getDemoApiResponse(args, api.getState());

    if (demoResponse !== undefined) {
      return { data: demoResponse };
    }

    const isLoginRequest = args.url === "/auth/token/";
    const clientId = isLoginRequest ? await waitForMetricaClientId() : undefined;

    const requestArgs = isLoginRequest ? {
      ...args,
      body: {
        ...args.body,
        ...(clientId ? { client_id: clientId } : {}),
      },
    } : args;

    const path = typeof args === 'string' ? args : args.url;
    const isPublicRequest = path?.replace(/^\//, '').startsWith('auth/') || args.meta?.suppressUnauthorizedRedirect;
    const canRefresh = () => !isDemoPage();
    const session = !isPublicRequest && canRefresh() ? getRememberedSession() : undefined;
    const sessionKey = getSessionKey();

    const requestRefresh = async (refresh: string): Promise<AuthRefreshResult> => {
      const controller = new AbortController();
      const result = await rawBaseQuery(
        { url: '/auth/token/refresh/', method: 'POST', body: { refresh }, timeout: 15000 },
        { ...api, endpoint: REFRESH_ENDPOINT, signal: controller.signal, abort: () => controller.abort() },
        extraOptions,
      );

      const error = result.error;
      const status = error?.status === 'PARSING_ERROR' ? error.originalStatus : error?.status;

      return { data: result.data as AuthRefreshResult['data'], status };
    };

    if (session) {
      scheduleSessionRefresh({ requestRefresh, canRefresh, onExpired: expireRememberedSession });
    }

    let result = await rawBaseQuery(requestArgs, api, extraOptions);
    let refreshUnavailable = false;

    if (result.error?.status === 401 && session && !api.signal.aborted) {
      const outcome = await refreshRememberedSession({ session, requestRefresh });
      refreshUnavailable = outcome === 'unavailable';

      if (outcome === 'refreshed' && getSessionKey() === sessionKey && !api.signal.aborted) {
        result = await rawBaseQuery(requestArgs, api, extraOptions);
      } else if (outcome === 'rejected' && canRefresh()) {
        expireRememberedSession(session.id);
      }
    }

    const isUnauthorized = result.error?.status === 401;
    const isForbidden = result.error?.status === 403;

    if (isDemoPage() && isForbidden) {
      return { data: null };
    }

    const canRedirect = !isPublicRequest && !isPublicAuthPage();
    const hasCurrentSession = sessionKey === getSessionKey();
    const shouldRedirectUser = isUnauthorized && canRedirect && hasCurrentSession && !refreshUnavailable;

    const suppressAccessDenied = typeof args === 'object' && args !== null && args.meta?.suppressAccessDenied;

    if (isForbidden && !suppressAccessDenied) {
      api.dispatch(setAccessDenied(true));
    }

    if (shouldRedirectUser) {
      removeToken();
      redirectToLogin();
    }

    return result;
  };
}

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: baseQuery(),
  extractRehydrationInfo(action, { reducerPath }) {
    if (action.type === REHYDRATE) return action.payload[reducerPath]
  },
  endpoints: (builder) => ({
    signIn: builder.mutation<SignInOutput, SignInput>({
      query: (body) => ({url: '/auth/token/', method: 'POST', body}),
    }),
    signUp: builder.mutation<SignInOutput, SignUpInput>({
      query: (body) => ({url: 'auth/register/', method: 'POST', body}),
    }),
    getUser: builder.mutation<MeParamsOutput, unknown>({
      query: () => ({url: 'users/me/'}),
    }),
    updateUserInfo: builder.mutation<UpdateUserInfoParamsOutput, UpdateUserInfoParamsOutput>({
      query: (body) => ({ url: `users/update/`, method: 'POST', body }),
      async onQueryStarted() {
        await getIpInfo();
      },
    }),
    resetPassword: builder.mutation<unknown, ResetPasswordInput>({
      query: (body) => ({url: 'auth/reset_password/',  method: 'POST', body}),
    }),
    updatePassword: builder.mutation<unknown, ChangePasswordInput>({
      query: (body) => ({url: 'auth/reset_password/confirm/',  method: 'POST', body}),
    }),
  }),
})

export const authSlice = createSlice({
  name: 'userParams',
  initialState: {
    me: {} as MeParamsOutput,
    accessDenied: false,
  },
  reducers: {
    setAccessDenied: (state, { payload }: { payload: boolean }) => {
      state.accessDenied = payload;
    }
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      authApi.endpoints.signIn.matchFulfilled,
      (state, { payload }) => {
        if (payload?.user) {
          state.me = payload.user;
        }
        state.accessDenied = false;
      },
    )
    builder.addMatcher(
      authApi.endpoints.getUser.matchFulfilled,
      (state, { payload }) => {
        state.me = payload;
      },
    )
    builder.addMatcher(
      authApi.endpoints.updateUserInfo.matchFulfilled,
      (state, { payload }) => {
        state.me = { ...state.me, ...payload}
      }
    )
  },
})

export const { setAccessDenied } = authSlice.actions;

export const useSignInMutation = () => {
  return authApi.useSignInMutation({ fixedCacheKey: 'signIn' })
}

export const useSignUpMutation = () => {
  return authApi.useSignUpMutation({ fixedCacheKey: 'signUp' })
}

export const useGetUserMutation = () => {
  return authApi.useGetUserMutation({ fixedCacheKey: 'getUser' })
}

export const useResetPasswordMutation = () => {
  return authApi.useResetPasswordMutation({ fixedCacheKey: 'resetPassword' })
}

export const useUpdatePasswordMutation = () => {
  return authApi.useUpdatePasswordMutation({ fixedCacheKey: 'updatePassword' })
}

export const useUser = () => {
  return useSelector(({ userParams }: ReturnType<typeof rootReducer>) => userParams.me)
}

export const useUserHasPermission = (permission: string) => {
  const user = useUser();
  return user.permissions?.[permission] === true;
}

export const useAccessDenied = () => {
  return useSelector(({ userParams }: ReturnType<typeof rootReducer>) => Boolean(userParams?.accessDenied))
}

export const useUserIsManager = () => {
  const user = useUser();
  if (user.id) {
    const userHasManagerRole = user.groups ? Boolean(user.groups.filter((group) => group.name === USER_GROUPS.MANAGER)?.length) : false;
    return userHasManagerRole;
  }
}

export const useUserPermission = (key: string) => {
  const user = useUser();
  if (!user?.id) return false;
  if (user.permissions == null) return true;
  return Boolean(user.permissions[key]);
}

/** Только явное `staff.applications` (не путать с menu: при `permissions == null` useUserPermission даёт true). */
export const useStaffApplications = () => useUserHasPermission('staff.applications');

export const useUserIsBroker = () => {
  const user = useUser();
  if (user.id) {
    return user?.company_profile?.company.type === BROKER;
  }
}

export const useUpdateUserInfoMutation = () => {
  return authApi.useUpdateUserInfoMutation({ fixedCacheKey: 'updateUserInfo' })
}
